
import React from 'react';
import { ShieldAlert, Users, Zap, Wrench, MessageSquare, Target, TreeDeciduous, TrendingUp, CheckCircle } from 'lucide-react';

const SystemDifference: React.FC = () => {
  return (
    <section className="py-24 bg-[#F4E4C1] text-[#0F1419]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-7xl font-black mb-6 uppercase leading-[0.9] italic tracking-tighter">
            Why We're Different:<br /><span className="text-[#1A4D2E]">You Get A Team, Not Just A Tool</span>
          </h2>
          <p className="text-xl max-w-3xl mx-auto font-bold text-[#4E3629] leading-tight">
            I didn't scale my tree service by buying software. I did it by having the right systems, accountability, and someone who knew what actually works in the field. That's what you're getting here.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* Column 1: Most CRMs */}
          <div className="bg-white/50 border-4 border-[#4E3629]/10 p-8 md:p-12 rounded-[2.5rem] shadow-xl relative overflow-hidden">
            <h3 className="text-2xl font-black mb-8 uppercase flex items-center gap-3 text-gray-400 italic">
              <ShieldAlert className="w-8 h-8" /> Most CRMs
            </h3>
            <ul className="space-y-6">
              {[
                "Hand you software and say \"Figure it out yourself\"",
                "Generic templates that sound like a robot wrote them",
                "No accountability—they bill you even if you don't grow",
                "Support is a ticket system with \"tech support\" agents",
                "You're just a subscription number on a spreadsheet"
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="mt-1.5 w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
                  <p className="font-bold opacity-60 italic">{text}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 2: Arbor Advantage */}
          <div className="bg-[#1A4D2E] text-white p-8 md:p-12 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border-4 border-[#FF6B35]/20">
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
              <TreeDeciduous className="w-40 h-40" />
            </div>
            <h3 className="text-2xl font-black mb-8 uppercase flex items-center gap-3 text-[#FF6B35] italic">
              <Target className="w-8 h-8" /> Arbor Advantage
            </h3>
            <ul className="space-y-6 relative z-10">
              <li className="flex items-start gap-4">
                <Zap className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                <p className="font-black text-xl">We implement it FOR you (White Glove Setup)</p>
              </li>
              <li className="flex items-start gap-4">
                <Users className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                <p className="font-black text-xl">Weekly coaching calls with other tree owners</p>
              </li>
              <li className="flex items-start gap-4">
                <MessageSquare className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                <p className="font-black text-xl">Scripts from my real 7-figure tree business</p>
              </li>
              <li className="flex items-start gap-4">
                <TrendingUp className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                <p className="font-black text-xl">We track your numbers WITH YOU to ensure ROI</p>
              </li>
              <li className="flex items-start gap-4">
                <Wrench className="w-6 h-6 text-[#FF6B35] flex-shrink-0 mt-1" />
                <p className="font-black text-xl">Direct access to our team—not a support ticket</p>
              </li>
            </ul>
            <div className="mt-10 p-6 bg-white/5 rounded-2xl border border-white/10 italic font-bold">
              "You're a partner, not a number. We don't win unless your phone is ringing."
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemDifference;
